import { notification } from 'antd/lib'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useExamTimeLimit } from './use-exam-time-limit'
import { useGetAttempt } from './use-get-attempt'
import { AttemptStatus } from 'Types/Exams.types'

export const useExamAttempt = () => {
    const navigate = useNavigate()
    const params = useParams()

    const {
        data: attemptData,
        isLoading,
        error,
        refetch: fetchAttempt,
    } = useGetAttempt(params.attemptId)

    const { getTimeLeft, isTimeLimitSet } = useExamTimeLimit({
        timeLimitInSeconds: attemptData?.attempt.exam.time_limit_in_seconds,
        attempt: attemptData?.attempt,
    })

    useEffect(() => {
        if (error) {
            notification.error({
                message: (error as Error).message ?? 'Error in starting exam',
                description:
                    'Please try again. If you still face the issue, reach out to the project owner',
                placement: 'bottomLeft',
            })
            navigate(`/`, { replace: true })
        }

        if (isTimeLimitSet && !getTimeLeft()) {
            notification.error({
                message: 'Exam time limit exceeded',
                description: 'Cannot continue with the exam',
                placement: 'bottomLeft',
            })
            navigate(`/`, { replace: true })
        }

        if (
            attemptData?.attempt.status === AttemptStatus.COMPLETE ||
            attemptData?.attempt.status === AttemptStatus.INCOMPLETE
        ) {
            notification.info({
                message: 'Cannot attempt exam',
                description: 'Already completed the exam attempt, Start new attempt if remaining',
                placement: 'bottomLeft',
            })
            navigate(`/exam/${attemptData.attempt.exam.id}`, {
                replace: true,
            })
        }
    }, [
        error,
        navigate,
        isTimeLimitSet,
        getTimeLeft,
        attemptData?.attempt.status,
        attemptData?.attempt.exam.id,
        attemptData?.attempt.id,
    ])

    const data = useMemo(() => {
        if (!attemptData) {
            return
        }
        const answerIds = attemptData.attempt.answers
        return {
            questionIndex: attemptData.attempt.attempting_question_index,
            answerIds,
            examId: attemptData.attempt.exam.id,
            attemptId: attemptData.attempt.id,
            examType: attemptData.attempt.exam.type,
            isArchived: attemptData.attempt.exam?.is_archived,
            showExamResult: attemptData.attempt.exam.show_result,
        }
    }, [attemptData])

    return {
        data,
        isLoading,
        error,
        timeLimit: getTimeLeft(),
        refetch: fetchAttempt,
    }
}
